import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { ReferralFriendStatus, normalizeTypedReferralCode, shouldOfferCodeEntry } from '@cf-shared/data-model/referral';
import { ReferralFriendRow } from '@cf-shared/referral-interface';
import { lockClick } from 'directives/cs-capture-cover/cs-capture-cover.directive';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';
import { ToastType } from 'components/toast/toast-host';
import { AlertButtonRole, AlertController } from 'providers/alert-controller';
import { CsPage } from 'providers/nav-controller';
import { ToastController } from 'providers/toast-controller';
import { ReferralService, REFERRAL_REFUSAL_MESSAGES } from 'services/referral.service';
import { TelemetryService } from 'services/telemetry.service';
import { trySystemShare } from 'services/system-share';
import { ReferralHubPageParams } from './referral-hub-interface';

/** What the top of the page is showing. */
export enum ReferralHubLoadState {
  Loading = 'loading',
  Ready = 'ready',
  Failed = 'failed',
}

/**
 * The wording for each status, as one map rather than a `switch` with a default.
 *
 * Written this way the compiler refuses the file if a status is ever added to the
 * shared model and nobody wrote wording for it — the same reason
 * `REFERRAL_REFUSAL_MESSAGES` in the service is a map. A `default` arm would
 * silently show a blank label for a status we could have named.
 */
const REFERRAL_FRIEND_STATUS_LABELS: Record<ReferralFriendStatus, () => TranslatedString> = {
  [ReferralFriendStatus.Joined]: () => LL.referral.hub.friends.statusJoined(),
  [ReferralFriendStatus.Training]: () => LL.referral.hub.friends.statusTraining(),
  [ReferralFriendStatus.Member]: () => LL.referral.hub.friends.statusMember(),
};

/**
 * The page an inviter shares their invite from (#4627, extended by #4963).
 *
 * Phase 1 showed the link, the readable code, the message that goes with them and
 * a share button. Phase 3 adds the list underneath: how many friends this person
 * has brought in, and one row per friend with their first name and one of three
 * statuses. It is the only thing the inviter ever gets — recognition, never a
 * reward. See design-docs/referral-program.md.
 *
 * **The list is loaded separately from the invite and cannot take it down.** They
 * are two server calls and either can fail on its own; a friend list that failed
 * must never leave somebody unable to see or share their own link, which is what
 * the page is FOR.
 *
 * It also carries the fallback for the OTHER side of the programme: somebody who
 * was invited but whose link did not carry the referral through can type their
 * friend's code by hand here. That box is only useful to a person who has no
 * inviter recorded yet, which is why the server tells us whether they do.
 */
@Component({
  selector: 'page-referral-hub',
  templateUrl: 'referral-hub.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralHubPage extends CsPage<ReferralHubPageParams> {
  protected LL = LL;
  protected ReferralHubLoadState = ReferralHubLoadState;

  protected loadState = ReferralHubLoadState.Loading;
  protected inviteLink = '';
  protected code = '';

  /** The words that go with the link. Editable, because the product spec asks for it. */
  protected message = '';

  /** Whether to offer the "were you invited?" box. */
  protected isCodeEntryOffered = false;

  protected enteredCode = '';
  protected isCodeEntryBusy = false;
  /** The outcome of the last code somebody typed, already worded for them. Empty when there is none. */
  protected codeEntryMessage: TranslatedString | '' = '';

  /** The friends this person has brought in. Its own state, because its own server call. */
  protected friendsLoadState = ReferralHubLoadState.Loading;
  protected friends: ReferralFriendRow[] = [];

  constructor(
    private referral: ReferralService,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private telemetry: TelemetryService,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  async ionViewWillEnter(): Promise<void> {
    // seeded here rather than in a field initializer so the wording follows the
    // language in force when the page opens
    if (!this.message) this.message = LL.referral.hub.defaultMessage();

    void this.loadAllAsync();
  }

  /** Loads the invite and the list of friends. */
  private async loadAllAsync(): Promise<void> {
    // Both loads are started together and awaited together, so the list does not
    // wait on the invite. `allSettled` rather than `all`: each already handles its
    // own failure, and `all` would abandon the other the moment one rejected.
    await Promise.allSettled([this.loadInviteAsync(), this.loadFriendsAsync()]);
  }

  /**
   * Loads the list of friends.
   *
   * A failure shows a line and a retry rather than being swallowed: an empty list
   * and a list we could not fetch look identical, and telling somebody they have
   * invited nobody when we simply could not ask would be a lie about their own
   * effort. The commonest cause by far is being offline, which is why the report
   * goes through the service's severity split.
   */
  private async loadFriendsAsync(): Promise<void> {
    this.friendsLoadState = ReferralHubLoadState.Loading;
    this.safeMarkForCheck(this.cdRef);

    try {
      const result = await this.referral.listFriendsAsync();
      if (this.viewDestroyed) return;
      this.friends = result.friends;
      this.friendsLoadState = ReferralHubLoadState.Ready;
    } catch (err) {
      if (this.viewDestroyed) return;
      this.referral.reportCallFailure('referral-hub-friends-load-failed', err);
      this.friendsLoadState = ReferralHubLoadState.Failed;
    }

    this.safeMarkForCheck(this.cdRef);
  }

  /**
   * The name to show for one friend.
   *
   * The server sends an empty name for somebody who has set none, and the stand-in
   * is chosen HERE rather than there: this page is shown in the reader's own
   * language, and a word picked on the server would arrive in English inside a
   * Dutch list.
   */
  protected friendName(friend: ReferralFriendRow): string {
    return friend.firstName || LL.referral.hub.friends.noName();
  }

  protected friendStatusLabel(friend: ReferralFriendRow): TranslatedString {
    return REFERRAL_FRIEND_STATUS_LABELS[friend.status]();
  }

  protected onRetryFriendsClick(event: Event): void {
    lockClick(event, this.loadFriendsAsync());
  }

  private async loadInviteAsync(): Promise<void> {
    this.loadState = ReferralHubLoadState.Loading;
    this.safeMarkForCheck(this.cdRef);

    try {
      const result = await this.referral.getOrCreateCodeAsync();
      if (this.viewDestroyed) return;

      this.inviteLink = result.inviteLink;
      this.code = result.code;
      this.isCodeEntryOffered = shouldOfferCodeEntry(result.hasReferrer);
      this.loadState = ReferralHubLoadState.Ready;
      // fired on a SUCCESSFUL load rather than on page entry: an open that showed
      // the person nothing but an error is not a view of the hub, and counting it
      // as one would hide exactly the failure worth noticing
      this.telemetry.trackEvent('referral_hub_viewed', {});
    } catch (err) {
      if (this.viewDestroyed) return;
      // Shown rather than swallowed: without a link there is nothing on this page
      // to do, so a silent empty page would look broken. The user is offered a
      // retry, which is the only useful action — the commonest cause by far is
      // being offline.
      this.referral.reportCallFailure('referral-hub-load-failed', err);
      this.loadState = ReferralHubLoadState.Failed;
    }

    this.safeMarkForCheck(this.cdRef);
  }

  protected onRetryClick(event: Event): void {
    lockClick(event, this.loadInviteAsync());
  }

  protected onShareClick(event: Event): void {
    lockClick(event, this.shareInviteAsync());
  }

  /**
   * Hand the invite to the system share sheet, falling back to the clipboard.
   *
   * The link is passed SEPARATELY from the message and must never be pasted into
   * the message too: Android's share plugin joins the two itself, so a message
   * ending in the link would show it twice. `trySystemShare`'s own comment is the
   * authority on that.
   *
   * The clipboard fallback is the opposite case — there is only one destination,
   * so the message and the link are joined by hand.
   */
  private async shareInviteAsync(): Promise<void> {
    const text = this.referral.buildShareMessage(this.message);

    const result = await trySystemShare({
      title: LL.referral.hub.title(),
      url: this.inviteLink,
      text,
    });

    // 'cancelled' means the user dismissed the sheet on purpose — falling back to
    // the clipboard there would override a decision they just made
    if (result === 'shared') {
      this.telemetry.trackEvent('referral_link_shared', { share_method: 'share_sheet' });
      return;
    }
    if (result === 'cancelled') return;

    const copied = await this.copyToClipboardAsync(`${text} ${this.inviteLink}`);
    if (copied) this.telemetry.trackEvent('referral_link_shared', { share_method: 'clipboard' });
  }

  /** Copy the invite link on its own, from the tap-to-copy box. */
  protected onCopyLinkClick(event: Event): void {
    lockClick(event, this.copyInviteLinkAsync());
  }

  private async copyInviteLinkAsync(): Promise<void> {
    const copied = await this.copyToClipboardAsync(this.inviteLink);
    // counted as a share for the same reason the fallback above is: the person
    // now has the invite on their clipboard to paste wherever they meant to.
    // The CODE box below deliberately does not count, because the event is about
    // the link and the code is not the link.
    if (copied) this.telemetry.trackEvent('referral_link_shared', { share_method: 'clipboard' });
  }

  /** Copy the short code on its own, for somebody who is reading it out or typing it. */
  protected onCopyCodeClick(event: Event): void {
    lockClick(event, this.copyToClipboardAsync(this.code));
  }

  /**
   * Put text on the clipboard, and say whether it worked.
   *
   * One copy of this, because the clipboard is the one thing on this page that
   * can fail quietly. `navigator.clipboard` does not exist at all in a
   * non-secure context and `writeText` can be turned down by the browser —
   * unguarded, the rejection travels out and the user sees nothing whatever:
   * they tapped, and the page did not react.
   */
  private async copyToClipboardAsync(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      this.referral.reportCallFailure('referral-share-clipboard-failed', err);
      await this.alertCtrl.showAlertAsync({
        message: LL.referral.hub.copyFailed(),
        enableBackdropDismiss: true,
        buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary }],
      });
      return false;
    }

    this.toastCtrl.showToast({
      message: LL.referral.hub.copiedToClipboard(),
      type: ToastType.SuccessLight,
      duration: 2000,
    });
    return true;
  }

  protected onApplyCodeClick(event: Event): void {
    lockClick(event, this.applyEnteredCodeAsync());
  }

  private async applyEnteredCodeAsync(): Promise<void> {
    // tidied before anything looks at it: the alphabet is upper-case only, and a
    // phone keyboard produces lower case after the first character, so an
    // untouched value would refuse a perfectly correct code
    const typed = normalizeTypedReferralCode(this.enteredCode);
    if (!typed || this.isCodeEntryBusy) return;

    this.isCodeEntryBusy = true;
    this.codeEntryMessage = '';
    this.safeMarkForCheck(this.cdRef);

    try {
      const result = await this.referral.claimAsync(typed);
      if (this.viewDestroyed) return;

      if (result.claimed) {
        this.telemetry.trackEvent('referral_signup', { referral_source: 'typed_code' });
        this.codeEntryMessage = LL.referral.hub.wereYouInvited.accepted();
        // the box has done its job; leaving it up invites a second code that
        // would certainly be refused
        this.isCodeEntryOffered = false;
      } else {
        this.codeEntryMessage = result.refusal
          ? REFERRAL_REFUSAL_MESSAGES[result.refusal]()
          : LL.referral.hub.wereYouInvited.refusals.unexpectedError();
      }
    } catch (err) {
      if (this.viewDestroyed) return;
      // a thrown error is different from a refusal: the server never got to
      // decide, so say so plainly rather than implying the code was rejected
      this.referral.reportCallFailure('referral-hub-claim-failed', err);
      this.codeEntryMessage = LL.referral.hub.wereYouInvited.refusals.unexpectedError();
    } finally {
      this.isCodeEntryBusy = false;
      this.safeMarkForCheck(this.cdRef);
    }
  }
}
